'use client';

/**
 * HTML5 Video Modal
 *
 * Modal component for displaying HTML5 video elements with full tracking support.
 * Uses native HTML5 video events for analytics integration.
 */

import React, { Suspense, useEffect, useCallback, useRef, useMemo } from 'react';
import { Modal } from '../Modal';
import { IconCta } from '../../../composables';
import { useVideoTracking } from '../../../hooks/useVideoTracking';
import { createVideoAnalyticsConfig } from '../../../utils/analytics/videoHelpers';
import type { MediaSource, AnalyticsProps } from '../../../types';
import { classNames } from '../../../utils/classNames';

interface Html5VideoModalProps {
  isOpen: boolean;
  src: string;
  onClose: () => void;
  mediaSource: MediaSource & { type: 'embedCmsVideo' };
  videoMetadata?: {
    title?: string;
    id?: string;
    duration?: number;
  };
  analyticsConfig?: AnalyticsProps;
  className?: string;
}

export const Html5VideoModal: React.FC<Html5VideoModalProps> = ({
  isOpen,
  onClose,
  src,
  mediaSource,
  videoMetadata,
  analyticsConfig,
  className,
}) => {
  const videoRef = useRef<HTMLVideoElement>(null);
  const progressIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const hasStartedRef = useRef(false);

  // Generate tracking config internally
  const trackingConfig = useMemo(() => {
    const videoData = createVideoAnalyticsConfig(
      mediaSource,
      videoMetadata,
      analyticsConfig?.sectionHeader
    );

    return {
      ...videoData,
      moduleName: analyticsConfig?.moduleName || 'video-section',
    };
  }, [mediaSource, videoMetadata, analyticsConfig]);

  const { trackVideoStart, trackVideoProgress, trackVideoPause, trackVideoExit, markVideoResumed } =
    useVideoTracking(trackingConfig);

  /**
   * Handle video play event - track start
   */
  const handlePlay = useCallback(() => {
    const video = videoRef.current;
    if (!video || hasStartedRef.current) return;

    hasStartedRef.current = true;
    const duration = video.duration;

    // Track video start
    trackVideoStart(duration);

    // Start progress tracking interval
    if (progressIntervalRef.current) {
      clearInterval(progressIntervalRef.current);
    }

    progressIntervalRef.current = setInterval(() => {
      if (video) {
        trackVideoProgress(video.currentTime, video.duration);
      }
    }, 1000); // Check every second

    markVideoResumed();
  }, [trackVideoStart, trackVideoProgress, markVideoResumed]);

  /**
   * Handle video pause event
   */
  const handlePause = useCallback(() => {
    const video = videoRef.current;
    if (!video) return;

    if (progressIntervalRef.current) {
      clearInterval(progressIntervalRef.current);
      progressIntervalRef.current = null;
    }

    trackVideoPause(video.currentTime, video.duration);
  }, [trackVideoPause]);

  /**
   * Handle video ended event - track 100% completion
   */
  const handleEnded = useCallback(() => {
    const video = videoRef.current;
    if (!video) return;

    if (progressIntervalRef.current) {
      clearInterval(progressIntervalRef.current);
      progressIntervalRef.current = null;
    }

    // Track 100% completion
    trackVideoProgress(video.duration, video.duration);
  }, [trackVideoProgress]);

  /**
   * Handle modal close with exit tracking
   */
  const handleClose = useCallback(
    (method: 'close_button' | 'escape' | 'backdrop' = 'close_button') => {
      // Pause video before closing
      if (videoRef.current && !videoRef.current.paused) {
        videoRef.current.pause();
      }

      trackVideoExit(method);

      // Clear progress interval
      if (progressIntervalRef.current) {
        clearInterval(progressIntervalRef.current);
        progressIntervalRef.current = null;
      }

      // Reset start flag for next open
      hasStartedRef.current = false;

      onClose();
    },
    [trackVideoExit, onClose]
  );

  const handleCloseButton = useCallback(() => {
    handleClose('close_button');
  }, [handleClose]);

  const handleBackdropClick = useCallback(() => {
    handleClose('backdrop');
  }, [handleClose]);

  // Handle ESC key
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && isOpen) {
        handleClose('escape');
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleKeyDown);
    }

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [isOpen, handleClose]);

  // Reset start flag when modal opens
  useEffect(() => {
    if (isOpen) {
      hasStartedRef.current = false;
    }
  }, [isOpen]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (progressIntervalRef.current) {
        clearInterval(progressIntervalRef.current);
      }
    };
  }, []);

  return (
    <Suspense fallback={null}>
      {isOpen && (
        <Modal
          isOpen={isOpen}
          onClose={handleBackdropClick}
          className={classNames('modal-player__modal', className)}
        >
          <IconCta
            icon="close"
            onClick={handleCloseButton}
            ariaLabel="Close video"
            className="modal-player__close-button"
            size="large"
          />

          <div className="modal-player__video-content">
            <video
              ref={videoRef}
              src={src}
              autoPlay
              playsInline
              controls
              className="modal-player__video"
              onPlay={handlePlay}
              onPause={handlePause}
              onEnded={handleEnded}
            >
              Your browser does not support the video tag.
            </video>
          </div>
        </Modal>
      )}
    </Suspense>
  );
};
